'use client'

import { useAtomValue, useStore } from 'jotai'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useOptionSymbolMap } from '@/features/chart/hooks/use-option-symbol-map'
import {
  buildDispatchOrderSelection,
  resolveRepeatAmount,
} from '@/features/dashboard/lib/order-row-actions'
import { useChatMessages } from '@/features/order/chat/hooks/use-chat-state'
import {
  type ChoiceRequest,
  choiceRequestFromResponse,
} from '@/features/order/choice-picker/lib/choice-request'
import {
  chatResponseToOrderDraft,
  type OrderPromptDraft,
} from '@/features/order/order-form/lib/order-prompt'
import { useMultiExchangeInstrumentMap } from '@/hooks/use-multi-exchange-instrument-map'
import { useOrderFormDispatch } from '@/hooks/use-order-form-dispatch'
import { orderQuantitySide } from '@/lib/order-denomination'
import { toExchangeEnum, tradeVenueToExchangeKey } from '@/lib/proto/util'
import { resolveOrderFormTarget } from '@/lib/resolve-order-form-target'
import type { ChatMessage } from '@/lib/types'
import { useUserAccounts } from '@/service/account.hook'
import { useSendChatMessage } from '@/service/ai.hook'
import {
  orderFormDenomPreferenceAtomFamily,
  orderFormExecutedCountAtomFamily,
  orderFormPromptFillAtomFamily,
  orderFormSummaryTabAtomFamily,
} from '@/state/order'
import type { Account } from '@/types/generated/common/account'

export type OrderPromptStatus =
  | { kind: 'idle' }
  | { kind: 'loaded'; message: string; priceDropped?: boolean }
  | {
      kind: 'reply'
      message: string
      /** The backend asked instead of guessing, with answers to pick from or a format to type. */
      choice?: ChoiceRequest
      /** The @mention the question was asked under; an answer is sent under it too. */
      mention?: Account | null
      /** The account the question was asked under, whose venue and positions the answers name. */
      account?: Account | null
    }
  /** Multi-step or conditional: more than one form can hold, so nothing was loaded. */
  | { kind: 'plan'; message: string }
  | { kind: 'error'; message: string }

/**
 * The Order Form's prompt bar — the one place to trade by prompt: the text goes to the trading
 * backend and its ticket (one order, or one leg per selected instrument) is loaded into this form
 * for the user to review and submit. Anything
 * else it answers is shown as a note under the bar. One server session covers one order, so a
 * follow-up ("make it 0.2") refines the current draft — and a fresh session starts once that draft
 * is executed (the form submits) or dismissed, or on "New order", so an old draft's context never
 * leaks into the next order.
 */
export function useOrderPrompt(cardId: string) {
  const store = useStore()
  const [status, setStatus] = useState<OrderPromptStatus>({ kind: 'idle' })
  const [feedback, setFeedback] = useState<{
    chatId: string
    turnIndex: number
    message: ChatMessage
  } | null>(null)
  const multiExchangeInstrumentMap = useMultiExchangeInstrumentMap()
  const optionSymbolMap = useOptionSymbolMap()
  const { data: accountsData } = useUserAccounts({ showDisabled: true })
  const accounts = useMemo(() => accountsData?.accounts ?? [], [accountsData?.accounts])
  const {
    getDenomPreference,
    setFieldSignal,
    setOrderbookAmount,
    setOrderbookPrice,
    setOrderFormTab,
    setSelectedInstruments,
    setSelectedOptions,
  } = useOrderFormDispatch(cardId)
  const threadId = `order-prompt:${cardId}`
  const { mutateAsync, isPending, account } = useSendChatMessage({ cardId, threadId })
  const { chatId, messages, setMessages, resetChat } = useChatMessages(threadId)

  const startNewSession = useCallback(() => {
    resetChat()
    setStatus({ kind: 'idle' })
    setFeedback(null)
  }, [resetChat])

  // The form submitted an order (whatever filled it): that draft is done, start over.
  const executedCount = useAtomValue(orderFormExecutedCountAtomFamily(cardId))
  const seenExecutedCountRef = useRef(executedCount)
  useEffect(() => {
    if (executedCount === seenExecutedCountRef.current) return
    seenExecutedCountRef.current = executedCount
    startNewSession()
  }, [executedCount, startNewSession])

  const applyDraft = useCallback(
    (draft: OrderPromptDraft): boolean => {
      const legs = draft.legs.map((leg) => {
        const account = accounts.find((a) => a.accountId === leg.accountId)
        const exchange =
          leg.exchange ??
          (account ? toExchangeEnum(tradeVenueToExchangeKey(account.tradeVenue)) : undefined)
        return { leg, exchange }
      })
      const [first] = legs
      if (!first || legs.some(({ exchange }) => exchange === undefined)) return false

      // Show the amount in the unit the user asked in ("$500 of BTC" stays $500): when it
      // doesn't already match the form's Base/Quote setting, switch the setting. The setting is
      // form-wide, so the first leg decides.
      const unit = orderQuantitySide(first.leg.denomination)
      const sameUnitAmount = resolveRepeatAmount(
        {
          symbol: first.leg.symbol,
          quantity: first.leg.quantity,
          denomination: first.leg.denomination,
          exchange: first.exchange!,
        },
        multiExchangeInstrumentMap,
        optionSymbolMap,
        getDenomPreference()
      )
      if (unit && sameUnitAmount === undefined) {
        store.set(orderFormDenomPreferenceAtomFamily(resolveOrderFormTarget(store, cardId)), unit)
      }

      // The prompt describes the whole ticket, so it replaces what was there. Each leg carries
      // its own side and size.
      const selections = legs.map(({ leg, exchange }) =>
        buildDispatchOrderSelection(leg, exchange!, multiExchangeInstrumentMap, optionSymbolMap)
      )
      const targetId = resolveOrderFormTarget(store, cardId)
      const tabBefore = store.get(orderFormSummaryTabAtomFamily(targetId))
      setSelectedOptions(selections.flatMap((s) => ('option' in s ? [s.option] : [])))
      setSelectedInstruments(selections.flatMap((s) => ('instrument' in s ? [s.instrument] : [])))
      setOrderFormTab(draft.tab)
      // Flash what this prompt filled in (prompt-fill-flash.tsx), so the change is visible.
      store.set(orderFormPromptFillAtomFamily(targetId), (prev) => ({
        nonce: prev.nonce + 1,
        at: Date.now(),
        legs: selections.map((s) =>
          'option' in s ? s.option.instrumentName : s.instrument.symbol
        ),
        price: !!draft.price,
        tab: tabBefore !== draft.tab,
      }))
      if (legs.length === 1) setOrderbookAmount(first.leg.quantity)
      if (draft.price) setOrderbookPrice(draft.price)
      if (Object.keys(draft.fields).length > 0) setFieldSignal(draft.fields)
      return true
    },
    [
      accounts,
      cardId,
      getDenomPreference,
      multiExchangeInstrumentMap,
      optionSymbolMap,
      setFieldSignal,
      setOrderbookAmount,
      setOrderbookPrice,
      setOrderFormTab,
      setSelectedInstruments,
      setSelectedOptions,
      store,
    ]
  )

  /**
   * Resolves true when the prompt was used up — an order was loaded, or the backend answered
   * (often a question like "how long should the TWAP run?"), so the field should clear for the
   * reply. False (plan, unmappable draft, error) keeps the text to edit and resend.
   */
  const submit = useCallback(
    async (rawText: string, options?: { account?: Account | null }): Promise<boolean> => {
      const text = rawText.trim()
      if (!text) return false

      try {
        // `account`: the bar's @mention — this prompt only, the card's account stays as it is.
        const response = await mutateAsync({ prompt: text, account: options?.account })
        const previousMessages = response.sessionId === chatId ? messages : []
        const turnIndex =
          previousMessages.filter((message) => message.role === 'assistant').length + 1
        const message: ChatMessage = {
          id: `${response.sessionId}-ai-${turnIndex}`,
          role: 'assistant',
          parts: [{ type: 'text', text: response.message }],
        }
        setMessages([
          ...previousMessages,
          {
            id: `${response.sessionId}-user-${turnIndex}`,
            role: 'user',
            parts: [{ type: 'text', text }],
          },
          message,
        ])
        setFeedback({ chatId: response.sessionId, turnIndex, message })
        const outcome = chatResponseToOrderDraft(response)
        if (outcome.kind === 'plan') {
          setStatus({ kind: 'plan', message: outcome.message })
          return false
        }
        if (outcome.kind === 'reply') {
          setStatus({
            kind: 'reply',
            message: outcome.message,
            choice: choiceRequestFromResponse(response) ?? undefined,
            mention: options?.account,
            account: options?.account ?? account,
          })
          return true
        }
        if (!applyDraft(outcome.draft)) {
          setStatus({ kind: 'reply', message: outcome.message })
          return false
        }
        setStatus({
          kind: 'loaded',
          message: outcome.message,
          priceDropped: outcome.draft.priceDropped,
        })
        return true
      } catch (error) {
        setFeedback(null)
        // Rejects with an ErrorResponse ({status, message}), not always an Error.
        const message = (error as { message?: unknown })?.message
        setStatus({ kind: 'error', message: typeof message === 'string' ? message : '' })
        return false
      }
    },
    [account, applyDraft, chatId, messages, mutateAsync, setMessages]
  )

  return {
    submit,
    isPending,
    status,
    feedback,
    /** Closing the note drops the draft it describes, so it also starts a fresh session. */
    dismiss: startNewSession,
    startNewSession,
    /** A server session is open — the next prompt would refine its draft. */
    hasSession: !!chatId,
  }
}
